import { useCalculator, STAGE_TIMING_GUIDANCE } from "@/context/CalculatorContext"

function formatNum(n: number, decimals = 0) {
  if (!isFinite(n) || isNaN(n)) return "0"
  return n.toFixed(decimals)
}

const STAGE_TYPE_LABELS: Record<string, string> = {
  preferment: "Preferment",
  autolyse: "Autolyse",
  soaker: "Soaker",
  custom: "Custom Stage",
}

/**
 * Hidden on screen, rendered only when printing (window.print / Save as PDF).
 * Mirrors the on-screen data from CalculatorContext into a clean, kitchen-ready
 * single-column layout: master formula, true hydration, and every dough stage
 * with its own ingredient breakdown plus the final-mix remainder.
 */
export default function PrintableFormula() {
  const {
    ingredients,
    stages,
    formulaName,
    totalDoughWeight,
    totalTrueHydration,
    flourWeight,
    getStageBreakdown,
    finalDoughRemainder,
    finalDoughWeight,
    bulkFermentationTime,
    finalProofTime,
  } = useCalculator()

  const printDate = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

  return (
    <div className="print-only print-formula">
      <header className="pb-4 mb-6 border-b-2 border-gray-900">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">
          BakersCalculator.com Formula Sheet
        </p>
        <h1 className="text-2xl font-bold text-gray-900">
          {formulaName?.trim() ? formulaName : "Untitled Formula"}
        </h1>
        <p className="text-xs text-gray-500 mt-1">Printed {printDate}</p>
      </header>

      {/* Master formula table */}
      <section className="mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-2">
          Master Formula
        </h2>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-400">
              <th className="text-left py-1.5 font-semibold text-gray-700">Ingredient</th>
              <th className="text-right py-1.5 font-semibold text-gray-700">Weight (g)</th>
              <th className="text-right py-1.5 font-semibold text-gray-700">Baker's %</th>
            </tr>
          </thead>
          <tbody>
            {ingredients.map((ing) => (
              <tr key={ing.id} className="border-b border-gray-200">
                <td className="py-1.5 text-gray-800">
                  {ing.name}
                  {ing.locked ? " (100% basis)" : ""}
                </td>
                <td className="py-1.5 text-right tabular-nums text-gray-800">
                  {formatNum(ing.weight)}
                </td>
                <td className="py-1.5 text-right tabular-nums text-gray-800">
                  {formatNum(ing.percentage, 1)}%
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-900">
              <td className="py-2 font-bold text-gray-900">Total Dough Weight</td>
              <td className="py-2 text-right font-bold tabular-nums text-gray-900">
                {formatNum(totalDoughWeight)} g
              </td>
              <td className="py-2 text-right font-bold tabular-nums text-gray-900">
                {formatNum(totalTrueHydration, 1)}% hyd.
              </td>
            </tr>
          </tfoot>
        </table>
        <p className="text-xs text-gray-500 mt-1">
          Baker's percentage basis: Total Flour = {formatNum(flourWeight)} g (100%)
        </p>
      </section>

      {/* Dough stages */}
      {stages.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-2">
            Multi-Stage Dough Plan
          </h2>
          <div className="space-y-4">
            {stages.map((stage) => {
              const breakdown = getStageBreakdown(stage.id)
              const rows = ingredients
                .map((ing) => {
                  const alloc = stage.allocations.find((a) => a.ingredientId === ing.id)
                  const pct = alloc?.percentage ?? 0
                  const weight = (ing.weight * pct) / 100
                  return { ing, pct, weight }
                })
                .filter((r) => r.pct > 0)

              return (
                <div key={stage.id} className="print-formula" style={{ pageBreakInside: "avoid" }}>
                  <div className="flex items-baseline justify-between border-b border-gray-400 pb-1 mb-1.5">
                    <h3 className="text-sm font-bold text-gray-900">
                      {stage.name}{" "}
                      <span className="text-xs font-normal text-gray-500">
                        ({STAGE_TYPE_LABELS[stage.type] || "Stage"})
                      </span>
                    </h3>
                    {stage.restTime ? (
                      <span className="text-xs text-gray-500">Rest: {stage.restTime}</span>
                    ) : null}
                  </div>
                  {STAGE_TIMING_GUIDANCE[stage.type] ? (
                    <p className="text-xs text-gray-500 mb-1.5">
                      Guidance: {STAGE_TIMING_GUIDANCE[stage.type].tempRange}, {STAGE_TIMING_GUIDANCE[stage.type].durationRange} &mdash;{" "}
                      {STAGE_TIMING_GUIDANCE[stage.type].tip}
                    </p>
                  ) : null}
                  <table className="w-full text-xs border-collapse">
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.ing.id} className="border-b border-gray-100">
                          <td className="py-1 text-gray-700">{r.ing.name}</td>
                          <td className="py-1 text-right tabular-nums text-gray-700">
                            {formatNum(r.pct)}% of ingredient
                          </td>
                          <td className="py-1 text-right tabular-nums text-gray-700 w-20">
                            {formatNum(r.weight)} g
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex gap-4 mt-1.5 text-xs text-gray-600">
                    <span>
                      Stage Weight: <strong className="tabular-nums">{formatNum(breakdown.weight)} g</strong>
                    </span>
                    <span>
                      Flour/Water:{" "}
                      <strong className="tabular-nums">
                        {formatNum(breakdown.flourWeight)}/{formatNum(breakdown.waterWeight)} g
                      </strong>
                    </span>
                    <span>
                      Hydration: <strong className="tabular-nums">{formatNum(breakdown.hydration, 1)}%</strong>
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Final mix remainder */}
          <div className="mt-4 pt-3 border-t-2 border-gray-900" style={{ pageBreakInside: "avoid" }}>
            <div className="flex items-baseline justify-between mb-1.5">
              <h3 className="text-sm font-bold text-gray-900">Final Mix (After Stages)</h3>
              <span className="text-sm font-bold tabular-nums text-gray-900">
                {formatNum(finalDoughWeight)} g
              </span>
            </div>
            <table className="w-full text-xs border-collapse">
              <tbody>
                {finalDoughRemainder
                  .filter((item) => item.remainingWeight > 0.01)
                  .map((item) => (
                    <tr key={item.id} className="border-b border-gray-100">
                      <td className="py-1 text-gray-700">{item.name}</td>
                      <td className="py-1 text-right tabular-nums text-gray-700">
                        {formatNum(item.remainingWeight)} g to add at final mix
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Baking day timeline */}
      {(bulkFermentationTime?.trim() || finalProofTime?.trim()) ? (
        <section className="mb-6" style={{ pageBreakInside: "avoid" }}>
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-2">
            Baking Day Timeline
          </h2>
          <table className="w-full text-sm border-collapse">
            <tbody>
              {bulkFermentationTime?.trim() ? (
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 font-semibold text-gray-800 w-48">Bulk Fermentation</td>
                  <td className="py-1.5 text-gray-700">{bulkFermentationTime}</td>
                </tr>
              ) : null}
              {finalProofTime?.trim() ? (
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 font-semibold text-gray-800 w-48">Final Proof (Shaped)</td>
                  <td className="py-1.5 text-gray-700">{finalProofTime}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>
      ) : null}

      <footer className="mt-8 pt-3 border-t border-gray-300 text-xs text-gray-400">
        Generated with BakersCalculator.com &mdash; the free baker's percentage &amp; sourdough hydration engine.
      </footer>
    </div>
  )
}
